// src/services/radiographyService.js
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";
const MAX_RADIOGRAPH_FILE_BYTES = 5 * 1024 * 1024;
const radiographBase64Cache = new Map();
const radiographUrlCache = new Map();

const getToken = () => localStorage.getItem("token") || "";

const estimateBase64Bytes = (base64) => {
  if (typeof base64 !== "string") return 0;
  const parts = base64.split(",");
  const payload = (parts[1] || parts[0] || "").replace(/\s/g, "");
  if (!payload) return 0;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.floor((payload.length * 3) / 4) - padding;
};

const ensureBase64 = async (fileOrBase64) => {
  if (fileOrBase64 instanceof File || fileOrBase64 instanceof Blob) {
    if (fileOrBase64.size > MAX_RADIOGRAPH_FILE_BYTES) {
      throw new Error("Radiografia trebuie să aibă maximum 5MB");
    }
    const data = await fileToBase64(fileOrBase64);
    return { base64: data, fileName: fileOrBase64.name || "radiografie.png" };
  }
  if (typeof fileOrBase64 === "string" && fileOrBase64.trim()) {
    const normalized = fileOrBase64.trim();
    if (estimateBase64Bytes(normalized) > MAX_RADIOGRAPH_FILE_BYTES) {
      throw new Error("Radiografia trebuie să aibă maximum 5MB");
    }
    return { base64: normalized, fileName: "radiografie.png" };
  }
  throw new Error("Tip invalid pentru radiografie");
};

const request = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || "Eroare radiografie");
    error.payload = data;
    throw error;
  }
  return data;
};

const emitRadiographChanged = (appointmentId, action) => {
  if (typeof window === "undefined" || !appointmentId) return;
  window.dispatchEvent(
    new CustomEvent("radiograph:changed", {
      detail: { appointmentId, action },
    })
  );
};

const setRadiographCache = (appointmentId, base64) => {
  if (!appointmentId || !base64) return;
  radiographBase64Cache.set(appointmentId, base64);
  const existingUrl = radiographUrlCache.get(appointmentId);
  if (existingUrl) {
    URL.revokeObjectURL(existingUrl);
  }
  const blob = base64ToBlob(base64);
  const nextUrl = URL.createObjectURL(blob);
  radiographUrlCache.set(appointmentId, nextUrl);
};

const clearRadiographCache = (appointmentId) => {
  if (!appointmentId) return;
  radiographBase64Cache.delete(appointmentId);
  const existingUrl = radiographUrlCache.get(appointmentId);
  if (existingUrl) {
    URL.revokeObjectURL(existingUrl);
  }
  radiographUrlCache.delete(appointmentId);
};

export async function saveRadiograph(appointmentId, fileOrBase64) {
  try {
    if (!appointmentId) {
      throw new Error("Lipsește ID-ul programării");
    }

    const { base64, fileName } = await ensureBase64(fileOrBase64);

    await request(`${API_URL}/api/radiographs/${appointmentId}`, {
      method: "POST",
      body: JSON.stringify({ data: base64, fileName }),
    });
    setRadiographCache(appointmentId, base64);
    emitRadiographChanged(appointmentId, "saved");
    return true;
  } catch (error) {
    console.error("saveRadiograph error:", error);
    return false;
  }
}

export async function loadRadiograph(appointmentId) {
  try {
    if (!appointmentId) return null;
    const cached = radiographBase64Cache.get(appointmentId);
    if (cached) return cached;
    const data = await request(`${API_URL}/api/radiographs/${appointmentId}`);
    const base64 = data?.data?.radiograph?.data || null;
    if (base64) setRadiographCache(appointmentId, base64);
    return base64;
  } catch (error) {
    if (error?.payload?.message === "Radiograph not found" || error?.message === "Radiograph not found") {
      return null;
    }
    console.error("loadRadiograph error:", error);
    return null;
  }
}

export async function deleteRadiograph(appointmentId) {
  try {
    if (!appointmentId) return false;
    await request(`${API_URL}/api/radiographs/${appointmentId}`, {
      method: "DELETE",
    });
    clearRadiographCache(appointmentId);
    emitRadiographChanged(appointmentId, "deleted");
    return true;
  } catch (error) {
    if (error?.payload?.message === "Radiograph not found") {
      clearRadiographCache(appointmentId);
      emitRadiographChanged(appointmentId, "deleted");
      return true;
    }
    console.error("deleteRadiograph error:", error);
    return false;
  }
}

export async function downloadRadiograph(appointmentId) {
  try {
    const base64 = await loadRadiograph(appointmentId);
    if (!base64) return;
    const blob = base64ToBlob(base64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${appointmentId}.png`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("downloadRadiograph error:", error);
  }
}

export async function getRadiographUrl(appointmentId) {
  if (appointmentId && radiographUrlCache.has(appointmentId)) {
    return radiographUrlCache.get(appointmentId);
  }
  const base64 = await loadRadiograph(appointmentId);
  if (!base64) return null;
  if (appointmentId && radiographUrlCache.has(appointmentId)) {
    return radiographUrlCache.get(appointmentId);
  }
  const blob = base64ToBlob(base64);
  return URL.createObjectURL(blob);
}

// helpers
function fileToBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onloadend = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}
function base64ToBlob(base64) {
  const [header, data] = base64.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const bin = atob(data);
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
}
